// Katalogos registry reader — surfaces every asset registered on-chain in
// the shared Katalogos to Potidaea, so a user who lists a new MBA via the
// Publish flow shows up in the dApp without a code redeploy.
//
// Scope of THIS version (v1 registry):
//   The v1 covenant only stores the CATEGORY ID (as an immutable NFT
//   commitment on the registry's own token address). It does NOT store
//   the ancillary constants a swap needs — rate, fee, founder locking
//   bytecode — which live in the deployed MBA contract's constructor and
//   are not recoverable from a plain address lookup. So this module reads
//   the CATEGORY LIST and pairs it against a static METADATA SEED for the
//   known categories. Unknown categories surface as "registered but not
//   swappable here yet" with just their id.
//
//   v2 registry (proposed in ADR-0055 Amendment 2026-09-02) carries symbol
//   + iconSha256 + iconPath in the entry NFT — enough to swap directly. The
//   swap picker will merge across versions when that ships.
//
// The read path is a single Electrum `listunspent` per registry version.
// The one live chipnet version has ~2-3 entries at time of writing; even
// heavy usage keeps this well under one round trip per boot.
//
// See wiki/asm/potidaea-asset-discovery.md for the flow diagram.

import { ElectrumClient, fetchAddressUtxos, fetchTransactionHex } from './electrum';
import { decodeTransaction, hexToBin as laHexToBin, binToHex as laBinToHex } from '@bitauth/libauth';
import type { KnownMba } from './orbital';

/** One live chipnet registry version — mirrors shells/app/src/core/katalogos.ts
 *  KATALOGOS_VERSIONS.chipnet[0]. Update in lockstep with that file (or add
 *  a shared source once a v2 lands). */
export const KATALOGOS_VERSIONS_CHIPNET = [
  {
    label: 'chipnet-v1-oracle-1usd',
    contractTokenAddress: 'bchtest:rwm0e4rnq0htx97g3sllrgld652w7ztxlnqlx7820kd3mhklk6v8vmtth22xs',
    registryCategoryId:   '04841b26ef63a7954e26227a37d104c83a3a75c94f1f88e9822d47896f021eea',
  },
] as const;

export interface RegisteredEntry {
  readonly categoryId: string;    // 32-byte hex, DISPLAY order
  readonly version: string;       // which registry it came from
  readonly utxo: string;          // txid:vout of the entry NFT (for provenance)
}

/** DVMB (Deviant Mint Bounds) — the OP_RETURN convention publish.ts writes
 *  alongside each register(). See publish.ts encodeMetadataBlob. */
const DVMB_MAGIC = new Uint8Array([0x44, 0x56, 0x4d, 0x42]);   // "DVMB"
const TEMPLATE_ARTEMISION_MINT_FT = 0x00;
const TEMPLATE_DEVIANT_SHARE_MBA  = 0x01;
const TEMPLATE_DEVIANT_ORACLE_MBA = 0x02;

export interface DiscoveredParams {
  templateId: number;
  // Common (ArtemisionMintFT + Share)
  rateSats?: bigint;
  // Fee — meaning depends on template (Artemision + Oracle: founder fee;
  // Share: total fee before split)
  feeBps?: bigint;
  // Artemision + Oracle
  founderPkh?: Uint8Array;
  // Share
  companySplitBps?: bigint;
  companyPkh?: Uint8Array;
  distributionPkh?: Uint8Array;
  // Oracle
  oraclePkHex?: string;
  // Display + icon (all templates)
  ticker?: string;
  name?: string;
  iconSha256Hex?: string;
  iconPath?: string;
}

/** For a chipnet entry, fetch the registration tx and try to decode the
 *  DVMB OP_RETURN sidecar. Returns null when the tx doesn't carry one —
 *  older / bare-v1 registrations remain listable but not swappable
 *  cross-user. */
export async function fetchEntryParams(
  client: ElectrumClient,
  entry: RegisteredEntry,
): Promise<DiscoveredParams | null> {
  const [txid] = entry.utxo.split(':');
  const hex = await fetchTransactionHex(client, txid);
  if (!hex) return null;
  const bytes = laHexToBin(hex);
  const decoded = decodeTransaction(bytes);
  if (typeof decoded === 'string') { console.warn('[registry] decodeTransaction:', decoded); return null; }
  for (const out of decoded.outputs) {
    const lb = out.lockingBytecode;
    if (lb.length < 6 || lb[0] !== 0x6a) continue;   // 0x6a = OP_RETURN
    // OP_RETURN payload: OP_PUSHDATA "DVMB" OP_PUSHDATA <blob>
    // Simplest parse — walk the script chunks.
    const chunks = readNullDataChunks(lb);
    if (chunks.length < 2) continue;
    if (!bytesEqual(chunks[0], DVMB_MAGIC)) continue;
    const blob = chunks[1];
    if (blob.length < 1) { console.warn('[registry] DVMB blob empty'); continue; }
    const dv = new DataView(blob.buffer, blob.byteOffset, blob.byteLength);
    const templateId = blob[0];
    // Per-template payload sizes — see publish.ts encodeMetadataBlob for
    // the byte layouts. A blob whose length doesn't match its template is
    // ignored (defensive against future publishers writing an unrecognised
    // shape into a slot we THINK we understand).
    let rateSats: bigint | undefined;
    let feeBps: bigint | undefined;
    let founderPkh: Uint8Array | undefined;
    let companySplitBps: bigint | undefined;
    let companyPkh: Uint8Array | undefined;
    let distributionPkh: Uint8Array | undefined;
    let oraclePkHex: string | undefined;
    if (templateId === TEMPLATE_ARTEMISION_MINT_FT) {
      if (blob.length !== 1 + 8 + 2 + 20) { console.warn('[registry] Artemision DVMB size', blob.length); continue; }
      rateSats = (BigInt(dv.getUint32(5, true)) << 32n) | BigInt(dv.getUint32(1, true));
      feeBps = BigInt(dv.getUint16(9, true));
      founderPkh = blob.slice(11, 31);
    } else if (templateId === TEMPLATE_DEVIANT_SHARE_MBA) {
      if (blob.length !== 1 + 8 + 2 + 2 + 20 + 20) { console.warn('[registry] Share DVMB size', blob.length); continue; }
      rateSats = (BigInt(dv.getUint32(5, true)) << 32n) | BigInt(dv.getUint32(1, true));
      feeBps = BigInt(dv.getUint16(9, true));
      companySplitBps = BigInt(dv.getUint16(11, true));
      companyPkh = blob.slice(13, 33);
      distributionPkh = blob.slice(33, 53);
    } else if (templateId === TEMPLATE_DEVIANT_ORACLE_MBA) {
      if (blob.length !== 1 + 2 + 20 + 33) { console.warn('[registry] Oracle DVMB size', blob.length); continue; }
      feeBps = BigInt(dv.getUint16(1, true));
      founderPkh = blob.slice(3, 23);
      oraclePkHex = laBinToHex(blob.slice(23, 56));
    } else {
      console.warn('[registry] unknown DVMB templateId 0x' + templateId.toString(16));
      continue;
    }
    // Optional label chunks. Anything non-ASCII / longer than the
    // conventional caps is treated as absent — a rogue publisher can't
    // inject arbitrarily large names into other wallets' UIs.
    const ticker = chunks[2] ? decodeLabel(chunks[2], 8) : undefined;
    const name   = chunks[3] ? decodeLabel(chunks[3], 32) : undefined;
    // Optional icon sidecar (ADR-0055 Amendment). iconSha256 must be
    // exactly 32 bytes; iconPath is a plain ASCII string capped at 96
    // bytes. Both must be present for the icon to have a chance of
    // rendering — a lone hash without a path can't be fetched, a lone
    // path without a hash can't be verified so it's discarded (silent
    // icon-swap resistance).
    let iconSha256Hex: string | undefined;
    let iconPath: string | undefined;
    if (chunks[4] && chunks[4].length === 32) {
      iconSha256Hex = laBinToHex(chunks[4]);
    }
    if (chunks[5]) iconPath = decodeLabel(chunks[5], 96);
    return {
      templateId,
      rateSats, feeBps,
      founderPkh,
      companySplitBps, companyPkh, distributionPkh,
      oraclePkHex,
      ticker, name, iconSha256Hex, iconPath,
    };
  }
  return null;
}

/** Resolve an iconPath (as inscribed in DVMB chunk 6) to a fetchable URL.
 *
 *  The convention is a FULL URL — content-addressing (SHA-256 in chunk 5)
 *  guarantees the bytes are the intended ones, so the reader can be liberal
 *  about where it fetches from. Anything without http/https scheme is
 *  refused (defence-in-depth against a rogue publisher pointing at
 *  file:// / javascript: / etc.). */
export function iconPathToUrl(iconPath: string): string | null {
  if (iconPath.startsWith('https://') || iconPath.startsWith('http://')) return iconPath;
  return null;
}

/** Fetch icon bytes and verify the SHA-256 matches the on-chain hash.
 *  Returns a data URL on success, null on any of:
 *    - unsupported/unresolvable path
 *    - network error / non-200
 *    - hash mismatch (silent-icon-swap attempt)
 *    - file too large (guard against a hostile path serving a giant file) */
export async function fetchAndVerifyIcon(
  iconPath: string,
  iconSha256Hex: string,
  maxBytes = 256 * 1024,
): Promise<string | null> {
  const url = iconPathToUrl(iconPath);
  if (!url) return null;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6_000);
    const res = await fetch(url, { signal: controller.signal, cache: 'no-store' });
    clearTimeout(timer);
    if (!res.ok) return null;
    const buf = new Uint8Array(await res.arrayBuffer());
    if (buf.byteLength > maxBytes) return null;
    const digest = await crypto.subtle.digest('SHA-256', buf);
    const got = laBinToHex(new Uint8Array(digest));
    if (got.toLowerCase() !== iconSha256Hex.toLowerCase()) {
      console.warn('[katalogos] icon hash mismatch for', url, '— expected', iconSha256Hex.slice(0, 12), 'got', got.slice(0, 12));
      return null;
    }
    // Content-type via response header, with a small fallback based on
    // path suffix for gateways that reply text/plain.
    const type = res.headers.get('content-type') || contentTypeForPath(iconPath) || 'application/octet-stream';
    return await bytesToDataUrl(buf, type);
  } catch (e) {
    console.warn('[katalogos] icon fetch failed for', iconPath, e instanceof Error ? e.message : e);
    return null;
  }
}

function contentTypeForPath(path: string): string | null {
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
  const map: Record<string, string> = {
    svg: 'image/svg+xml', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', webp: 'image/webp', ico: 'image/x-icon',
  };
  return map[ext] ?? null;
}

function bytesToDataUrl(bytes: Uint8Array, type: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([bytes], { type });
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function decodeLabel(bytes: Uint8Array, maxLen: number): string | undefined {
  if (bytes.length === 0 || bytes.length > maxLen) return undefined;
  // ASCII-printable filter: 0x20..0x7e. Drop the label entirely on any
  // control / non-ASCII byte to keep the swap picker's rendering safe.
  for (const b of bytes) if (b < 0x20 || b > 0x7e) return undefined;
  return new TextDecoder().decode(bytes);
}

/** Walk an OP_RETURN locking bytecode, returning each pushed data chunk. */
function readNullDataChunks(lb: Uint8Array): Uint8Array[] {
  const out: Uint8Array[] = [];
  let i = 1;   // skip 0x6a (OP_RETURN)
  while (i < lb.length) {
    const op = lb[i++];
    let len: number;
    if (op <= 0x4b) { len = op; }
    else if (op === 0x4c && i < lb.length) { len = lb[i++]; }
    else if (op === 0x4d && i + 1 < lb.length) { len = lb[i] | (lb[i + 1] << 8); i += 2; }
    else if (op === 0x4e && i + 3 < lb.length) { len = lb[i] | (lb[i + 1] << 8) | (lb[i + 2] << 16) | (lb[i + 3] << 24); i += 4; }
    else break;
    if (i + len > lb.length) break;
    out.push(lb.slice(i, i + len));
    i += len;
  }
  return out;
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function pkhToLB(pkh: Uint8Array): Uint8Array {
  const lb = new Uint8Array(25);
  lb[0] = 0x76; lb[1] = 0xa9; lb[2] = 0x14; lb.set(pkh, 3); lb[23] = 0x88; lb[24] = 0xac;
  return lb;
}
function categoryToConsensusLE(hex: string): Uint8Array {
  const b = laHexToBin(hex);
  const o = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) o[i] = b[b.length - 1 - i];
  return o;
}

/** For an entry whose DVMB params were decoded, derive a KnownMba record
 *  that orbital.ts can trade against. Dispatches by templateId:
 *
 *    0x00 Artemision      → ArtemisionMintFT contract address
 *    0x01 DeviantShareMBA → DeviantShareMBA contract address (SWAP not yet
 *                           wired in orbital.ts — the entry surfaces in the
 *                           picker as view-only until claimShareMBA lands)
 *    0x02 DeviantOracleMBA → DeviantOracleMBA contract address (same
 *                            view-only caveat — oracle-priced claim path
 *                            follows in a next pass)
 *
 *  The address derivation always runs so the resulting KnownMba can be
 *  used later without a re-derive; a Katalogos row that CAN'T claim yet
 *  still gets a live address for verification / block explorer links. */
export async function discoveredParamsToKnownMba(
  entry: RegisteredEntry,
  params: DiscoveredParams,
): Promise<KnownMba | null> {
  const { Contract, ElectrumNetworkProvider } = await import('cashscript');
  const provider = new ElectrumNetworkProvider('chipnet');
  const catLE = categoryToConsensusLE(entry.categoryId);
  const displayTicker = params.ticker ?? ('K:' + entry.categoryId.slice(0, 6));

  if (params.templateId === TEMPLATE_ARTEMISION_MINT_FT) {
    if (!params.founderPkh || params.rateSats == null || params.feeBps == null) return null;
    const founderLB = pkhToLB(params.founderPkh);
    const artifact = (await import('./artifacts/ArtemisionMintFT.json')).default;
    const contract = new Contract(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      artifact as any,
      [catLE, params.rateSats, params.feeBps, founderLB],
      { provider },
    );
    return {
      ticker: displayTicker,
      categoryId: entry.categoryId,
      templateId: params.templateId,
      rateSats: params.rateSats,
      founderFeeBps: params.feeBps,
      founderLockingBytecodeHex: laBinToHex(founderLB),
      contractAddress: contract.address,
      contractTokenAddress: contract.tokenAddress,
    };
  }

  if (params.templateId === TEMPLATE_DEVIANT_SHARE_MBA) {
    if (!params.companyPkh || !params.distributionPkh ||
        params.rateSats == null || params.feeBps == null || params.companySplitBps == null) return null;
    const companyLB = pkhToLB(params.companyPkh);
    const distributionLB = pkhToLB(params.distributionPkh);
    const artifact = (await import('./artifacts/DeviantShareMBA.json')).default;
    const contract = new Contract(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      artifact as any,
      [catLE, params.rateSats, params.feeBps, params.companySplitBps, companyLB, distributionLB],
      { provider },
    );
    return {
      ticker: displayTicker,
      categoryId: entry.categoryId,
      templateId: params.templateId,
      rateSats: params.rateSats,
      founderFeeBps: params.feeBps,   // reuse the slot; consumers gate on tpl below via KnownMbaExt
      founderLockingBytecodeHex: laBinToHex(companyLB),   // company treasury as the "founder" for display
      distributionLockingBytecodeHex: laBinToHex(distributionLB),
      companySplitBps: params.companySplitBps,
      contractAddress: contract.address,
      contractTokenAddress: contract.tokenAddress,
    };
  }

  if (params.templateId === TEMPLATE_DEVIANT_ORACLE_MBA) {
    if (!params.founderPkh || !params.oraclePkHex || params.feeBps == null) return null;
    const founderLB = pkhToLB(params.founderPkh);
    const artifact = (await import('./artifacts/DeviantOracleMBA.json')).default;
    const contract = new Contract(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      artifact as any,
      [catLE, laHexToBin(params.oraclePkHex), params.feeBps, founderLB],
      { provider },
    );
    return {
      ticker: displayTicker,
      categoryId: entry.categoryId,
      templateId: params.templateId,
      // Oracle: no fixed rate — leave 0 as a sentinel; swap picker will
      // learn to gate on template when the oracle claim path lands.
      rateSats: 0n,
      founderFeeBps: params.feeBps,
      founderLockingBytecodeHex: laBinToHex(founderLB),
      contractAddress: contract.address,
      contractTokenAddress: contract.tokenAddress,
    };
  }

  return null;
}

/** Fetch every category id registered at the given versions.
 *
 *  Duplicates (same category registered in >1 version) are folded first-seen.
 *  Failures per-version are swallowed and logged — the read is best-effort. */
export async function readKatalogos(
  client: ElectrumClient,
  versions: readonly typeof KATALOGOS_VERSIONS_CHIPNET[number][] = KATALOGOS_VERSIONS_CHIPNET,
): Promise<RegisteredEntry[]> {
  const out: RegisteredEntry[] = [];
  const seen = new Set<string>();
  for (const v of versions) {
    let utxos: Awaited<ReturnType<typeof fetchAddressUtxos>>;
    try {
      utxos = await fetchAddressUtxos(client, v.contractTokenAddress);
    } catch (e) {
      console.warn('[katalogos] read failed for', v.label, e instanceof Error ? e.message : e);
      continue;
    }
    for (const u of utxos) {
      const t = u.token_data;
      // Entry = immutable (no capability) NFT of the registry's category
      // whose 64-char-hex commitment is the target asset's category id.
      if (!t || t.category !== v.registryCategoryId) continue;
      const nft = t.nft;
      if (!nft || (nft.capability && nft.capability !== 'none')) continue;
      const commitment = nft.commitment;
      if (typeof commitment !== 'string' || commitment.length !== 64) continue;
      if (seen.has(commitment)) continue;
      seen.add(commitment);
      out.push({
        categoryId: commitment,
        version: v.label,
        utxo: `${u.tx_hash}:${u.tx_pos}`,
      });
    }
  }
  return out;
}
